import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { type AuditEvent, fetchAuditLogs, fetchTenant, type TenantDetail } from '@/api/superadmin';
import { Skeleton } from '@/components/primitives/skeleton';
import { EnvironmentKeysCard } from '@/components/superadmin/environment-keys-card';
import { SuperAdminPage } from '@/components/superadmin/superadmin-layout';
import { TenantAdminCard } from '@/components/superadmin/tenant-admin-card';

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-paragraph-sm text-text-soft">{label}</span>
      <span className="text-paragraph-sm text-text-strong text-right">{value}</span>
    </div>
  );
}

export function SuperAdminTenantDetailPage() {
  const { organizationId = '' } = useParams();
  const [tenant, setTenant] = useState<TenantDetail | null>(null);
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);

    try {
      const [detail, audit] = await Promise.all([
        fetchTenant(organizationId),
        // Best-effort: the tenant page is still useful without its history.
        fetchAuditLogs({ organizationId, limit: 10 }).catch(() => ({ events: [] as AuditEvent[] })),
      ]);

      setTenant(detail);
      setEvents(audit.events ?? []);
    } catch (caught) {
      setError((caught as Error).message);
    }
  }, [organizationId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) {
    return (
      <SuperAdminPage title="Tenant">
        <p className="text-paragraph-sm text-error-base">{error}</p>
        <Link to="/superadmin/tenants" className="text-paragraph-sm text-primary-base hover:underline">
          Back to tenants
        </Link>
      </SuperAdminPage>
    );
  }

  if (!tenant) {
    return (
      <SuperAdminPage title="Tenant">
        <Skeleton className="h-40 w-full" />
      </SuperAdminPage>
    );
  }

  return (
    <SuperAdminPage
      title={tenant.organization.name}
      description={`Created ${new Date(tenant.organization.createdAt).toLocaleDateString()}`}
      actions={
        <Link to="/superadmin/tenants" className="text-paragraph-sm text-text-sub hover:underline">
          ← All tenants
        </Link>
      }
    >
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <section className="border-stroke-soft bg-bg-white flex flex-col gap-3 rounded-xl border p-5">
          <h3 className="text-label-sm text-text-strong">Tenant information</h3>
          <Detail label="Name" value={tenant.organization.name} />
          <Detail label="Organization ID" value={tenant.organization._id} />
          <Detail label="Created" value={new Date(tenant.organization.createdAt).toLocaleString()} />
          <Detail label="Service level" value={tenant.organization.apiServiceLevel ?? '—'} />
          <Detail label="Members" value={String(tenant.organization.memberCount)} />
          <div className="border-stroke-soft mt-2 border-t pt-3">
            <Detail label="Subscribers" value={String(tenant.counts.subscribers)} />
            <Detail label="Workflows" value={String(tenant.counts.workflows)} />
            <Detail label="Messages (30 days)" value={String(tenant.counts.messagesLast30Days)} />
            <Detail
              label="Last activity"
              value={tenant.counts.lastActivityAt ? new Date(tenant.counts.lastActivityAt).toLocaleString() : '—'}
            />
          </div>
        </section>

        <div className="flex flex-col gap-4">
          <EnvironmentKeysCard environment={tenant.environments.development} name="Development" />
          <EnvironmentKeysCard environment={tenant.environments.production} name="Production" />
        </div>
      </div>

      <TenantAdminCard organizationId={organizationId} admin={tenant.admin} onChanged={() => void load()} />

      <section className="border-stroke-soft bg-bg-white flex flex-col gap-3 rounded-xl border p-5">
        <h3 className="text-label-sm text-text-strong">Recent activity</h3>
        {events.length === 0 ? (
          <p className="text-paragraph-sm text-text-soft">Nothing recorded for this tenant yet.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {events.map((event) => (
              <li key={event._id} className="flex items-center justify-between gap-4">
                <span className="text-paragraph-sm text-text-sub">
                  {event.action.replace(/_/g, ' ')}
                  {!event.success && <span className="text-error-base"> (failed)</span>}
                </span>
                <span className="text-paragraph-xs text-text-soft shrink-0">
                  {new Date(event.createdAt).toLocaleString()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </SuperAdminPage>
  );
}

export default SuperAdminTenantDetailPage;
